import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ROUNDTABLE_SESSIONS,
  isSessionLiveOrPast,
  isFounder,
  founderQS,
  saveRegistration,
} from "@/utils/founderAccess";

// ---------------------------------------------------------------
// /register — Session Registration Gate for the Roundtable.
//
// Three-step flow inside a single page:
//   1. Session Select   → pick one of the three broadcast windows
//   2. Registration     → name / company / email / phone (opt)
//   3. Countdown / Redirect → if session is live-or-past we hard
//      redirect to /roundtable; otherwise show a live-updating
//      countdown that auto-navigates when it hits zero.
//
// Founder mode carries through — `?founder=true` on inbound URL
// or a prior founder session persists via sessionStorage. The
// countdown-then-redirect flow always preserves the founder flag.
// ---------------------------------------------------------------

const GOLD = "#C9A84C";
const GOLD_SOFT = "rgba(201,168,76,0.55)";
const INK = "#0A0A0A";
const CARD = "#131313";
const HAIRLINE = "rgba(201,168,76,0.22)";
const MUTED = "#B7B7B0";
const IVORY = "#F5F1E3";
const API = process.env.REACT_APP_BACKEND_URL;
const SERIF = `'Playfair Display', 'Cormorant Garamond', Georgia, serif`;
const SANS = `'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, sans-serif`;

export default function Register() {
  const navigate = useNavigate();
  const founder = isFounder();

  const [step, setStep] = useState("select"); // 'select' | 'form' | 'countdown'
  const [sessionId, setSessionId] = useState(null);
  const [form, setForm] = useState({ name: "", company: "", email: "", phone: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const chosen = useMemo(
    () => ROUNDTABLE_SESSIONS.find((s) => s.id === sessionId) || null,
    [sessionId],
  );

  const setField = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const formValid =
    form.name.trim() && form.company.trim() && form.email.trim();

  const submit = async (e) => {
    if (e && typeof e.preventDefault === "function") e.preventDefault();
    if (!formValid || !chosen || loading) return;
    setError("");
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/roundtable-registrations`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name.trim(),
          company: form.company.trim(),
          email: form.email.trim(),
          phone: form.phone.trim(),
          session_choice: chosen.id,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        throw new Error(data.detail || `Registration failed (HTTP ${res.status})`);
      }
      // Persist locally so the /roundtable soft gate lets this
      // visitor through on any subsequent nav in this session.
      saveRegistration({
        session_choice: chosen.id,
        session_time: chosen.iso,
        registered_at: data.registered_at,
        name: form.name.trim(),
        company: form.company.trim(),
        email: form.email.trim(),
      });
      // If the session is already live or past, take them
      // straight to the stage. Otherwise show countdown.
      if (isSessionLiveOrPast(chosen.iso)) {
        navigate(`/roundtable${founderQS()}`);
      } else {
        setStep("countdown");
      }
    } catch (err) {
      console.error("[Register] submit failed:", err);
      setError(err.message || "Registration failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="register-root"
      style={{
        minHeight: "100vh",
        background: INK,
        color: IVORY,
        fontFamily: SANS,
        padding: "48px 20px 80px",
      }}
    >
      <style>{`
        @keyframes r-fade { from { opacity:0; transform: translateY(14px);} to { opacity:1; transform: translateY(0);} }
        .r-fade  { animation: r-fade 800ms ease-out both; }
        .r-fade2 { animation: r-fade 800ms ease-out both; animation-delay: 140ms; }
        .r-fade3 { animation: r-fade 800ms ease-out both; animation-delay: 280ms; }
        .r-card { transition: transform 260ms ease, box-shadow 260ms ease, border-color 260ms ease; }
        .r-card:hover { transform: translateY(-4px); border-color: ${GOLD}; box-shadow: 0 30px 60px -25px rgba(201,168,76,0.45); }
        .r-input:focus { border-color: ${GOLD} !important; box-shadow: 0 0 0 3px rgba(201,168,76,0.20); outline: none; }
        .r-cta { transition: transform 220ms ease, box-shadow 220ms ease, opacity 220ms ease; }
        .r-cta:not(:disabled):hover { transform: translateY(-1px); box-shadow: 0 20px 40px -14px ${GOLD}; }
      `}</style>

      <div style={{ maxWidth: 980, margin: "0 auto" }}>
        {/* Hero */}
        <div className="r-fade" style={{ textAlign: "center", marginBottom: 40 }}>
          <div
            data-testid="register-kicker"
            style={{
              display: "inline-block",
              padding: "6px 14px",
              border: `1px solid ${GOLD_SOFT}`,
              color: GOLD,
              fontSize: 11,
              letterSpacing: "0.42em",
              textTransform: "uppercase",
              fontWeight: 600,
              borderRadius: 999,
              marginBottom: 18,
            }}
          >
            {founder ? "Founder Access · Reserve Seat" : "Strike Three · Registration Open"}
          </div>
          <h1
            data-testid="register-title"
            style={{
              fontFamily: SERIF,
              fontSize: "clamp(2.2rem, 5vw, 3.4rem)",
              fontWeight: 700,
              margin: 0,
              lineHeight: 1.1,
              color: IVORY,
              letterSpacing: "-0.01em",
            }}
          >
            Select Your Briefing Window
          </h1>
          <p
            data-testid="register-subtitle"
            style={{
              marginTop: 14,
              color: MUTED,
              fontFamily: SERIF,
              fontStyle: "italic",
              fontSize: "1.05rem",
              lineHeight: 1.5,
              maxWidth: 640,
              marginLeft: "auto",
              marginRight: "auto",
            }}
          >
            The Ohio Landscaper Growth Roundtable — choose your session to meet the Council.
          </p>
        </div>

        {founder && (
          <FounderTag testid="register-founder-tag" />
        )}

        {/* Step 1 · Session select */}
        {step === "select" && (
          <div
            data-testid="register-session-select"
            className="r-fade2"
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
              gap: 18,
            }}
          >
            {ROUNDTABLE_SESSIONS.map((s) => {
              const live = isSessionLiveOrPast(s.iso);
              return (
                <div
                  key={s.id}
                  data-testid={`register-session-card-${s.id}`}
                  className="r-card"
                  style={{
                    background: CARD,
                    border: `1px solid ${HAIRLINE}`,
                    borderRadius: 14,
                    padding: "26px 24px",
                    display: "flex",
                    flexDirection: "column",
                    gap: 10,
                  }}
                >
                  <div
                    style={{
                      color: GOLD,
                      fontSize: 11,
                      letterSpacing: "0.32em",
                      textTransform: "uppercase",
                      fontWeight: 700,
                    }}
                  >
                    {s.label} {live && "· Live"}
                  </div>
                  <div
                    style={{
                      fontFamily: SERIF,
                      fontSize: "1.6rem",
                      color: IVORY,
                      fontWeight: 700,
                      lineHeight: 1.15,
                    }}
                  >
                    {s.dayLabel}
                  </div>
                  <div style={{ color: MUTED, fontSize: "0.95rem" }}>
                    {s.dateLabel}
                  </div>
                  <div
                    style={{
                      marginTop: 4,
                      color: GOLD,
                      fontFamily: SERIF,
                      fontStyle: "italic",
                      fontSize: "1.05rem",
                      letterSpacing: "0.06em",
                    }}
                  >
                    {s.timeLabel}
                  </div>
                  <button
                    type="button"
                    className="r-cta"
                    data-testid={`register-select-${s.id}`}
                    onClick={() => {
                      setSessionId(s.id);
                      setStep("form");
                    }}
                    style={{
                      marginTop: 16,
                      background: GOLD,
                      color: "#0A0A0A",
                      padding: "13px 20px",
                      fontSize: "0.85rem",
                      border: "none",
                      borderRadius: 8,
                      cursor: "pointer",
                      fontWeight: 800,
                      letterSpacing: "0.22em",
                      textTransform: "uppercase",
                      boxShadow: `0 14px 30px -14px ${GOLD}`,
                    }}
                  >
                    Select This Session
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {/* Step 2 · Registration form */}
        {step === "form" && chosen && (
          <div
            data-testid="register-form"
            className="r-fade2"
            style={{
              background: CARD,
              border: `1px solid ${HAIRLINE}`,
              borderRadius: 14,
              padding: "34px 32px",
              maxWidth: 640,
              margin: "0 auto",
            }}
          >
            <div
              style={{
                color: GOLD,
                fontSize: 11,
                letterSpacing: "0.32em",
                textTransform: "uppercase",
                fontWeight: 700,
                marginBottom: 8,
              }}
            >
              You&apos;re Registering For
            </div>
            <h2
              style={{
                fontFamily: SERIF,
                color: IVORY,
                margin: "0 0 6px",
                fontSize: "1.6rem",
                fontWeight: 700,
              }}
              data-testid="register-form-session-label"
            >
              {chosen.dayLabel}, {chosen.dateLabel}
            </h2>
            <div
              style={{
                color: GOLD,
                fontFamily: SERIF,
                fontStyle: "italic",
                fontSize: "1.05rem",
                marginBottom: 22,
              }}
            >
              {chosen.timeLabel}
            </div>

            <form onSubmit={submit} style={{ display: "grid", gap: 14 }}>
              <Field label="Full Name" testid="register-input-name" value={form.name} onChange={setField("name")} placeholder="Todd Vance" required />
              <Field label="Company Name" testid="register-input-company" value={form.company} onChange={setField("company")} placeholder="Dream Life Lawncare" required />
              <Field label="Email" testid="register-input-email" type="email" value={form.email} onChange={setField("email")} placeholder="you@yourcompany.com" required />
              <Field label="Phone (optional)" testid="register-input-phone" type="tel" value={form.phone} onChange={setField("phone")} placeholder="(555) 555-0134" />

              {error && (
                <div
                  data-testid="register-error"
                  style={{
                    marginTop: 4,
                    background: "rgba(239,68,68,0.10)",
                    border: "1px solid rgba(239,68,68,0.55)",
                    borderRadius: 8,
                    padding: "12px 14px",
                    color: "#fff",
                    fontSize: "0.95rem",
                  }}
                >
                  {error}
                </div>
              )}

              <div style={{ display: "flex", gap: 12, marginTop: 8, flexWrap: "wrap" }}>
                <button
                  type="button"
                  onClick={() => {
                    setStep("select");
                    setSessionId(null);
                    setError("");
                  }}
                  data-testid="register-back"
                  style={{
                    background: "transparent",
                    color: GOLD,
                    border: `1px solid ${GOLD_SOFT}`,
                    padding: "13px 20px",
                    borderRadius: 8,
                    fontFamily: SANS,
                    fontWeight: 700,
                    letterSpacing: "0.22em",
                    textTransform: "uppercase",
                    fontSize: "0.82rem",
                    cursor: "pointer",
                  }}
                >
                  ← Change Session
                </button>
                <button
                  type="submit"
                  className="r-cta"
                  data-testid="register-submit"
                  disabled={loading || !formValid}
                  style={{
                    background: GOLD,
                    color: "#0A0A0A",
                    border: "none",
                    padding: "13px 22px",
                    borderRadius: 8,
                    fontFamily: SANS,
                    fontWeight: 800,
                    letterSpacing: "0.24em",
                    textTransform: "uppercase",
                    fontSize: "0.82rem",
                    cursor: loading || !formValid ? "not-allowed" : "pointer",
                    opacity: loading || !formValid ? 0.55 : 1,
                    boxShadow: `0 14px 30px -14px ${GOLD}`,
                    flex: "1 1 220px",
                  }}
                >
                  {loading ? "Registering…" : "Complete Registration"}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Step 3 · Countdown / redirect */}
        {step === "countdown" && chosen && (
          <Countdown
            iso={chosen.iso}
            onArrive={() => navigate(`/roundtable${founderQS()}`)}
            founder={founder}
          />
        )}
      </div>
    </div>
  );
}

// ---- Field ---------------------------------------------------
function Field({ label, testid, value, onChange, placeholder, type = "text", required }) {
  return (
    <label
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 6,
        color: MUTED,
        fontSize: 11,
        letterSpacing: "0.26em",
        textTransform: "uppercase",
        fontWeight: 700,
      }}
    >
      {label}
      <input
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        required={required}
        data-testid={testid}
        className="r-input"
        style={{
          background: INK,
          color: IVORY,
          border: `1px solid ${HAIRLINE}`,
          borderRadius: 8,
          padding: "13px 15px",
          fontSize: "1em",
          fontFamily: SANS,
          letterSpacing: "normal",
          textTransform: "none",
          fontWeight: 400,
          transition: "border-color 200ms ease, box-shadow 200ms ease",
        }}
      />
    </label>
  );
}

// ---- Founder tag --------------------------------------------
function FounderTag({ testid }) {
  return (
    <div
      data-testid={testid || "founder-tag"}
      style={{
        margin: "0 auto 28px",
        maxWidth: 640,
        background: "linear-gradient(160deg, rgba(201,168,76,0.14) 0%, rgba(10,10,10,0.6) 90%)",
        border: `1px solid ${GOLD}`,
        borderRadius: 12,
        padding: "14px 20px",
        display: "flex",
        alignItems: "center",
        gap: 12,
        boxShadow: `0 24px 60px -30px rgba(201,168,76,0.55)`,
      }}
    >
      <span
        aria-hidden
        style={{
          width: 34,
          height: 34,
          borderRadius: "50%",
          background: GOLD,
          color: "#0A0A0A",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 18,
          fontWeight: 900,
          flexShrink: 0,
        }}
      >
        ★
      </span>
      <div>
        <div
          style={{
            color: GOLD,
            fontSize: 10,
            letterSpacing: "0.36em",
            textTransform: "uppercase",
            fontWeight: 700,
          }}
        >
          Founder Access Granted
        </div>
        <div style={{ color: IVORY, fontSize: "0.95rem", marginTop: 2 }}>
          Intelligence Tier already unlocked in your Command Center.
        </div>
      </div>
    </div>
  );
}

// ---- Countdown ----------------------------------------------
function CountdownCell({ n, label, testid }) {
  return (
    <div
      style={{
        background: CARD,
        border: `1px solid ${HAIRLINE}`,
        borderRadius: 12,
        padding: "18px 8px",
        textAlign: "center",
        minWidth: 92,
      }}
    >
      <div
        data-testid={testid}
        style={{
          fontFamily: SERIF,
          fontSize: "2.6rem",
          fontWeight: 700,
          color: IVORY,
          lineHeight: 1,
        }}
      >
        {String(n).padStart(2, "0")}
      </div>
      <div
        style={{
          marginTop: 6,
          color: GOLD,
          fontSize: 10,
          letterSpacing: "0.32em",
          textTransform: "uppercase",
          fontWeight: 700,
        }}
      >
        {label}
      </div>
    </div>
  );
}

function Countdown({ iso, onArrive, founder }) {
  const target = useMemo(() => new Date(iso).getTime(), [iso]);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    // Auto-redirect the moment we're within the live window.
    if (isSessionLiveOrPast(iso, new Date(now))) onArrive();
  }, [now, iso, onArrive]);

  const diff = Math.max(0, target - now);
  const secs = Math.floor(diff / 1000);
  const d = Math.floor(secs / 86400);
  const h = Math.floor((secs % 86400) / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = secs % 60;

  return (
    <div
      data-testid="register-countdown"
      className="r-fade2"
      style={{
        textAlign: "center",
        maxWidth: 720,
        margin: "0 auto",
        padding: "8px 8px 40px",
      }}
    >
      <div
        style={{
          color: GOLD,
          fontSize: 11,
          letterSpacing: "0.4em",
          textTransform: "uppercase",
          fontWeight: 700,
          marginBottom: 14,
        }}
      >
        Briefing Initializing…
      </div>
      <h2
        style={{
          fontFamily: SERIF,
          color: IVORY,
          fontSize: "clamp(1.8rem, 3.6vw, 2.6rem)",
          fontWeight: 700,
          margin: "0 0 10px",
        }}
      >
        You&apos;re on the list.
      </h2>
      <p style={{ color: MUTED, fontSize: "1rem", lineHeight: 1.55, margin: "0 0 32px" }}>
        The Council convenes {new Date(iso).toLocaleString(undefined, { weekday: "long", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
        {". "}When the countdown hits zero we&apos;ll take you straight to the stage.
      </p>
      <div
        style={{
          display: "flex",
          gap: 14,
          justifyContent: "center",
          flexWrap: "wrap",
          marginBottom: 32,
        }}
      >
        <CountdownCell n={d} label="Days"    testid="countdown-days" />
        <CountdownCell n={h} label="Hours"   testid="countdown-hours" />
        <CountdownCell n={m} label="Minutes" testid="countdown-minutes" />
        <CountdownCell n={s} label="Seconds" testid="countdown-seconds" />
      </div>
      <div style={{ color: MUTED, fontSize: "0.9rem", letterSpacing: "0.05em" }}>
        {founder ? "Founder access preserved · Intelligence Tier unlocked" : "Confirmation email dispatched"}
      </div>
    </div>
  );
}
